import { DefaultTheme, SemanticColorsMap, ThemeSemanticColors } from '@libs/types';
import { COLOR_SCHEME_ATTRIBUTE } from './colorScheme';

/**
 * Naming rule shared by every package: a semantic color token (`fill/secondary-transparent/hovered`) maps to the
 * CSS custom property `--` + token with `/` replaced by `_` (`--fill_secondary-transparent_hovered`).
 * The rule is mechanical on purpose — style files reference the variable by hand, and the theme writer in
 * `CoreProvider` derives the same name from a theme key, so no lookup table is needed on either side.
 */

const SEMANTIC_TOKEN_PATTERN = /^[a-z0-9]+(?:[/_-][a-z0-9]+)*$/;

/** Whether `token` is a well-formed semantic token (lowercase segments joined by `/`, `-` or `_`). */
export const isSemanticToken = (token: string): boolean => SEMANTIC_TOKEN_PATTERN.test(token);

/** CSS custom property name for a semantic token: `fill/primary/default` → `--fill_primary_default`. */
export const semanticTokenToCssVar = (token: string): string => `--${token.replaceAll('/', '_')}`;

/** `var()` reference for a semantic token: `fill/primary/default` → `var(--fill_primary_default)`. */
export const semanticVar = (token: string): string => `var(${semanticTokenToCssVar(token)})`;

// Theme values end up inside a <style> block, so only plain color syntax is let through: hex, named colors and
// color functions (rgb()/hsl()/oklch()…) — nothing that could close the declaration or the rule, or load a resource.
const SAFE_COLOR_VALUE = /^(?:#?[\w-]+|(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch|color)\([\w%.,\s/-]*\))$/i;

const COLOR_SCHEMES = [DefaultTheme.light, DefaultTheme.dark] as const;

const toDeclarations = (map: SemanticColorsMap | undefined) =>
    Object.entries(map ?? {})
        .filter(([token, value]) => isSemanticToken(token) && typeof value === 'string' && SAFE_COLOR_VALUE.test(value.trim()))
        .map(([token, value]) => `${semanticTokenToCssVar(token)}: ${value.trim()};`);

/**
 * CSS for a theme's resolved semantic colors: one rule per scheme, keyed off the `<html data-theme>` attribute
 * `CoreProvider` maintains. Only tokens the theme defines for that scheme are emitted, so anything missing keeps
 * the value from the package CSS. `html:root[…]` (0,2,1) outranks the package's `:root` (0,1,0), so the theme
 * wins regardless of stylesheet order. Packages with design versions (consumer) never declare the unprefixed
 * variable: their `--vN_<token>` defaults fall back from it, and a theme key prefixed with the version
 * (`v2/fill/primary/default`) overrides one version only.
 *
 * Works only in packages whose CSS also keys `color-scheme` off `data-theme`; invalid tokens and values that
 * are not plain colors are skipped.
 */
export const getSemanticColorsCss = (semanticColors?: ThemeSemanticColors): string =>
    COLOR_SCHEMES.map((scheme) => {
        const declarations = toDeclarations(semanticColors?.[scheme]);
        return declarations.length > 0 ? `html:root[${COLOR_SCHEME_ATTRIBUTE}="${scheme}"] {\n${declarations.join('\n')}\n}` : '';
    })
        .filter(Boolean)
        .join('\n');
